/** @jsxImportSource ./simboluri-jsx */
// PLANȘA AXULUI DE FUNDAȚIE — ce nu se putea desena până acum.
//
// Grinda de fundare e o bară pe pat de resoarte: momentul își schimbă semnul
// sub fiecare stâlp și între ei, deci „momentul de capăt" nu există. Până
// acum pasul raporta două cifre (M⁺ maxim, M⁻ maxim) și o singură carcasă
// constantă pe tot axul — din care nu se putea desena nicio planșă. Acum
// Pasul 12 dă ÎNFĂȘURĂTOAREA pe stații, iar Pasul 18 taie barele; aici se
// văd amândouă, una sub alta, pe aceeași scară de lungime:
//
//   1. M⁺/M⁻ pe lungime, cu stâlpii marcați (momentul se desenează pe fibra
//      ÎNTINSĂ — M⁺ în jos, M⁻ în sus, convenția de planșă);
//   2. A_s cerut (roșu) sub A_s efectiv (albastru), pe fiecare față: banda
//      dintre ele e rezerva, iar dacă roșul iese din albastru se vede exact
//      pe ce porțiune de ax — aceeași convenție cu `PuncteGrinda`;
//   3. ELEVAȚIA armăturii: carcasa continuă pe toată lungimea, barele
//      suplimentare pe intervalele lor (partea plină = cât lucrează, cea
//      punctată = decalarea a_l + ancorarea l_bd) și etrierii chiar la pasul
//      lor, zonă cu zonă;
//   4. |V| pe lungime, peste banda zonelor de etrieri.
//
// Nu inventează nimic: toate cifrele sunt din `ax.statii`, `ax.armare` și
// `ax.reazeme_m`. Unde serverul n-a dat înfășurătoare (rulare a Pasului 12
// dinaintea), componenta nu desenează o formă inventată — spune că
// lipsește.

import { useMemo } from "react";
import { useT } from "../i18n";
import { AxArmat, CroireAxFundare, BaraSuplimentara, ZonaEtrieri } from "./armareAx";
import { mFmt } from "./cote";
import { PAL } from "./paletaDesen";

// Culorile vin din paleta unică a desenelor (`paletaDesen.ts`) — aceeași
// grindă are aceeași culoare în plan, în secțiune și în 3D.
const CERERE = PAL.bad; // ce i se cere
const CAPACITATE = PAL.barJos; // ce poate
const OTEL_SUPL = PAL.barSupl; // barele care se taie
const ETRIER = PAL.etrieri;
const BETON = PAL.fundatie;
const BETON_LINIE = PAL.fundatieLinie;
const STALP = PAL.mut;

const ML = 52; // marginea din stânga: acolo stau valorile axelor
const MR = 14;

function fmt(v: number | null | undefined, z = 0): string {
  if (v == null || !isFinite(Number(v))) return "—";
  return Number(v).toFixed(z);
}

/** O bandă de desen: converteşte o valoare în ordonată. `v0` e valoarea de pe
 *  linia de zero (mijlocul benzii la diagramele cu semn, baza la cele
 *  pozitive). */
function scara(y0: number, h: number, vmax: number, simetric: boolean) {
  const v = Math.max(Math.abs(vmax), 1e-9);
  const zero = simetric ? y0 + h / 2 : y0 + h;
  const k = simetric ? h / 2 / v : h / v;
  return { zero, y: (val: number) => zero - k * val };
}

function polilinie(
  st: { s_m: number }[],
  x: (s: number) => number,
  y: (v: number) => number,
  val: (s: { s_m: number }) => number
): string {
  return st.map((p) => `${x(p.s_m).toFixed(1)},${y(val(p)).toFixed(1)}`).join(" ");
}

export default function InfasuratoareAx({
  ax,
  croire,
  latime = 860,
}: {
  ax: AxArmat;
  /** croiala barelor continue ale axului (Pasul 18) — din ea se desenează
   *  petrecerile, pe aceeași abscisă ca stațiile */
  croire?: CroireAxFundare | null;
  latime?: number;
}) {
  const tt = useT();
  const st = useMemo(() => ax.statii ?? [], [ax.statii]);
  const L = Number(ax.L_m) || 1;
  const supl = ax.armare?.suplimentare ?? { jos: [], sus: [] };
  const zone: ZonaEtrieri[] = ax.armare?.etrieri?.zone ?? [];
  const reazeme = ax.reazeme_m ?? [];
  const numeStalp = new Map<number, string>();
  for (const s of ax.stalpi ?? [])
    if (s.id_plan) numeStalp.set(Math.round(s.s_m * 100), s.id_plan);

  if (st.length < 2) {
    return (
      <div className="sg-muted" style={{ fontSize: 12 }}>
        Rezultatul ăsta n-are înfășurătoare pe lungime — Pasul 12 a rulat
        înaintea de când eforturile grinzilor de fundare erau două
        cifre pe tot axul. Rulează din nou Pasul 12 și Pasul 18 ca barele să se
        poată tăia și desena.
      </div>
    );
  }

  const W = latime;
  const x = (s: number) => ML + ((W - ML - MR) * Math.min(Math.max(s, 0), L)) / L;

  // --- benzile, de sus în jos ---------------------------------------------
  const H_M = 96; // momentul
  const H_AS = 86; // ariile de oțel
  const H_EL = 108; // elevația armăturii
  const H_V = 74; // tăietoarea
  const GOL = 26;
  const yM = 16;
  const yAs = yM + H_M + GOL;
  const yEl = yAs + H_AS + GOL;
  const yV = yEl + H_EL + GOL;
  const H = yV + H_V + 34;

  const mMax = Math.max(
    ...st.map((p) => Math.abs(Number(p.M_poz_knm ?? 0))),
    ...st.map((p) => Math.abs(Number(p.M_neg_knm ?? 0))),
    1
  );
  const asMax = Math.max(
    ...st.map((p) =>
      Math.max(
        Number(p.As_jos_nec_cm2 ?? 0),
        Number(p.As_sus_nec_cm2 ?? 0),
        Number(p.As_jos_ef_cm2 ?? 0),
        Number(p.As_sus_ef_cm2 ?? 0)
      )
    ),
    1
  );
  const vMax = Math.max(...st.map((p) => Math.abs(Number(p.V_kn ?? 0))), 1);

  const sM = scara(yM, H_M, mMax, true);
  const sAs = scara(yAs, H_AS, asMax, true);
  const sV = scara(yV, H_V, vMax, false);

  // elevația: secțiunea în T, la scară pe verticală în banda ei
  const Hc = Number(ax.H_c_m) || 1;
  const ht = Number(ax.h_talpa_m) || Hc / 3;
  const kz = (H_EL - 30) / Hc; // px pe metru de înălțime
  const zSus = yEl + 10;
  const zJos = zSus + Hc * kz;
  const zTalpa = zJos - ht * kz;
  const c = (Number(ax.acoperire_mm) || 50) / 1000;
  const zBaraSus = zSus + Math.max(c * kz, 3);
  const zBaraJos = zJos - Math.max(c * kz, 3);

  // etrierii, chiar la pasul lor — plafonati ca să nu desenăm 3000 de linii
  const etrieri: number[] = [];
  for (const z of zone) {
    const pas = Math.max(Number(z.pas_mm) || 200, 25) / 1000;
    const n = Math.floor(Math.max(z.L_m, 0) / pas);
    const rar = Math.max(1, Math.ceil((n + 1) / 220));
    for (let i = 0; i <= n; i += rar) etrieri.push(z.s1_m + i * pas);
  }

  const bara = (
    z: BaraSuplimentara,
    y: number,
    cheie: string,
    sus: boolean
  ) => (
    <g key={cheie}>
      {/* prelungirea (decalare + ancorare): punctată, fiindcă acolo bara
          există dar încă își dezvoltă efortul */}
      <line
        x1={x(z.s1_m)}
        x2={x(z.s2_m)}
        y1={y}
        y2={y}
        stroke={OTEL_SUPL}
        strokeWidth={2}
        strokeDasharray="3 3"
        opacity={0.75}
      />
      {/* porțiunea în care bara chiar lucrează */}
      <line
        x1={x(z.s1_teoretic_m)}
        x2={x(z.s2_teoretic_m)}
        y1={y}
        y2={y}
        stroke={OTEL_SUPL}
        strokeWidth={3.2}
      />
      {[z.s1_m, z.s2_m].map((s, i) => (
        <line
          key={i}
          x1={x(s)}
          x2={x(s)}
          y1={y - 4}
          y2={y + 4}
          stroke={OTEL_SUPL}
          strokeWidth={1.4}
        />
      ))}
      <text
        x={(x(z.s1_m) + x(z.s2_m)) / 2}
        y={sus ? y - 5 : y + 11}
        fontSize={9.5}
        textAnchor="middle"
        fill={OTEL_SUPL}
      >
        {z.n}Ø{z.phi_mm}
      </text>
    </g>
  );

  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: 620 }}>
        {/* ------- stâlpii: o linie verticală prin toate benzile ---------- */}
        {reazeme.map((s, i) => (
          <g key={`r${i}`}>
            <line
              x1={x(s)}
              x2={x(s)}
              y1={yM - 8}
              y2={yV + H_V + 4}
              stroke={STALP}
              strokeDasharray="2 4"
              strokeWidth={1}
              opacity={0.7}
            />
            <text
              x={x(s)}
              y={yV + H_V + 16}
              fontSize={9}
              textAnchor="middle"
              fill={STALP}
            >
              {numeStalp.get(Math.round(s * 100)) ?? mFmt(s)}
            </text>
          </g>
        ))}

        {/* ------- 1. MOMENTUL ------------------------------------------- */}
        <text x={4} y={yM - 4} fontSize={10} fill="var(--mut)">
          M [kN·m] — desenat pe fibra ÎNTINSĂ
        </text>
        <line x1={ML} x2={W - MR} y1={sM.zero} y2={sM.zero} stroke="#999" />
        <polygon
          points={
            polilinie(st, x, sM.y, (p) =>
              -Number((p as { M_neg_knm?: number }).M_neg_knm ?? 0)
            ) +
            " " +
            st
              .slice()
              .reverse()
              .map(
                (p) =>
                  `${x(p.s_m).toFixed(1)},${sM
                    .y(-Number(p.M_poz_knm ?? 0))
                    .toFixed(1)}`
              )
              .join(" ")
          }
          fill={CERERE}
          opacity={0.13}
        />
        <polyline
          points={polilinie(st, x, sM.y, (p) =>
            -Number((p as { M_poz_knm?: number }).M_poz_knm ?? 0)
          )}
          fill="none"
          stroke={CERERE}
          strokeWidth={1.6}
        />
        <polyline
          points={polilinie(st, x, sM.y, (p) =>
            -Number((p as { M_neg_knm?: number }).M_neg_knm ?? 0)
          )}
          fill="none"
          stroke={CERERE}
          strokeWidth={1.6}
        />
        <text x={4} y={sM.zero - H_M / 2 + 10} fontSize={9} fill={CERERE}>
          M⁻ {fmt(mMax)}
        </text>
        <text x={4} y={sM.zero + H_M / 2 - 3} fontSize={9} fill={CERERE}>
          M⁺ {fmt(mMax)}
        </text>

        {/* ------- 2. ARIILE DE OȚEL ------------------------------------- */}
        <text x={4} y={yAs - 4} fontSize={10} fill="var(--mut)">
          A_s [cm²] — cerut (roșu) vs. efectiv (albastru), sus ↑ / jos ↓
        </text>
        <line x1={ML} x2={W - MR} y1={sAs.zero} y2={sAs.zero} stroke="#999" />
        {(
          [
            ["sus", 1],
            ["jos", -1],
          ] as [string, number][]
        ).map(([fata, semn]) => (
          <g key={fata}>
            <polyline
              points={polilinie(
                st,
                x,
                sAs.y,
                (p) =>
                  semn *
                  Number(
                    (p as Record<string, number>)[`As_${fata}_ef_cm2`] ?? 0
                  )
              )}
              fill="none"
              stroke={CAPACITATE}
              strokeWidth={1.8}
            />
            <polyline
              points={polilinie(
                st,
                x,
                sAs.y,
                (p) =>
                  semn *
                  Number(
                    (p as Record<string, number>)[`As_${fata}_nec_cm2`] ?? 0
                  )
              )}
              fill="none"
              stroke={CERERE}
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
          </g>
        ))}
        <text x={4} y={sAs.zero - H_AS / 2 + 10} fontSize={9} fill="var(--mut)">
          sus {fmt(asMax, 1)}
        </text>
        <text x={4} y={sAs.zero + H_AS / 2 - 3} fontSize={9} fill="var(--mut)">
          jos {fmt(asMax, 1)}
        </text>

        {/* ------- 3. ELEVAȚIA ARMĂTURII --------------------------------- */}
        <text x={4} y={yEl - 4} fontSize={10} fill="var(--mut)">
          Elevația: carcasa continuă, barele suplimentare (plin = cât lucrează,
          punctat = a_l + l_bd) și etrierii la pasul lor
        </text>
        {/* betonul: inima sus, talpa jos */}
        <rect
          x={ML}
          y={zSus}
          width={W - MR - ML}
          height={zTalpa - zSus}
          fill={BETON}
          opacity={0.35}
          stroke={BETON_LINIE}
        />
        <rect
          x={ML}
          y={zTalpa}
          width={W - MR - ML}
          height={zJos - zTalpa}
          fill={BETON}
          opacity={0.55}
          stroke={BETON_LINIE}
        />
        {/* stâlpii, schițați deasupra */}
        {reazeme.map((s, i) => (
          <rect
            key={`sc${i}`}
            x={x(s) - 7}
            y={zSus - 9}
            width={14}
            height={9}
            fill={STALP}
            opacity={0.55}
          />
        ))}
        {/* etrierii */}
        {etrieri.map((xe, i) => (
          <line
            key={`e${i}`}
            x1={x(xe)}
            x2={x(xe)}
            y1={zBaraSus}
            y2={zBaraJos}
            stroke={ETRIER}
            strokeWidth={0.7}
            opacity={0.55}
          />
        ))}
        {/* carcasa continuă */}
        <line
          x1={ML}
          x2={W - MR}
          y1={zBaraSus}
          y2={zBaraSus}
          stroke={CAPACITATE}
          strokeWidth={3}
        />
        <line
          x1={ML}
          x2={W - MR}
          y1={zBaraJos}
          y2={zBaraJos}
          stroke={CAPACITATE}
          strokeWidth={3}
        />
        {/* PETRECERILE: bara se dedublează pe l₀, fără culoare nouă — e
            aceeași bară, de două ori. La o grindă de fundare încărcarea vine
            de jos, deci fața de sus lucrează în câmp și cea de jos peste
            stâlpi: petrecerile ies de la sine în locurile inverse față de o
            grindă de planșeu, fiindcă zona lor se citește din înfășurătoare. */}
        {(croire?.trasee ?? []).flatMap((t) =>
          t.innadiri.map((i, k) => {
            const y = (t.fata === "sus" ? zBaraSus : zBaraJos)
                      + (t.fata === "sus" ? 3.5 : -3.5);
            return (
              <g key={`pet-${t.fata}-${k}`}>
                <title>
                  {tt("struct.bare.petrecere", {
                    fata: t.fata,
                    bare: `${t.n}Ø${t.phi_mm}`,
                    l0: fmt(i.l0_m, 2),
                    s: fmt(i.s_m, 2),
                    zona: i.zona || tt("struct.bare.fara_zona"),
                  })}
                </title>
                <line x1={x(i.s_m)} x2={x(i.s2_m)} y1={y} y2={y}
                      stroke={i.fortata ? PAL.amb : CAPACITATE}
                      strokeWidth={3} />
                <text x={(x(i.s_m) + x(i.s2_m)) / 2}
                      y={t.fata === "sus" ? y + 11 : y - 5}
                      textAnchor="middle" fontSize={8.5}
                      fill={i.fortata ? PAL.amb : CAPACITATE}>
                  l₀ {fmt(i.l0_m, 2)}
                </text>
              </g>
            );
          })
        )}
        <text x={ML + 4} y={zBaraSus - 5} fontSize={9.5} fill={CAPACITATE}>
          {ax.armare.sus.n}Ø{ax.armare.sus.phi_mm} continue sus
        </text>
        <text x={ML + 4} y={zBaraJos + 11} fontSize={9.5} fill={CAPACITATE}>
          {ax.armare.jos.n}Ø{ax.armare.jos.phi_mm} continue jos
        </text>
        {/* barele suplimentare */}
        {(supl.sus ?? []).map((z, i) =>
          bara(z, zBaraSus + 7, `ss${i}`, true)
        )}
        {(supl.jos ?? []).map((z, i) =>
          bara(z, zBaraJos - 7, `sj${i}`, false)
        )}

        {/* ------- 4. TĂIETOAREA + zonele de etrieri --------------------- */}
        <text x={4} y={yV - 4} fontSize={10} fill="var(--mut)">
          |V| [kN] și zonele de etrieri
        </text>
        <polygon
          points={
            `${x(0)},${sV.zero} ` +
            polilinie(st, x, sV.y, (p) =>
              Math.abs(Number((p as { V_kn?: number }).V_kn ?? 0))
            ) +
            ` ${x(L)},${sV.zero}`
          }
          fill={CERERE}
          opacity={0.12}
        />
        <polyline
          points={polilinie(st, x, sV.y, (p) =>
            Math.abs(Number((p as { V_kn?: number }).V_kn ?? 0))
          )}
          fill="none"
          stroke={CERERE}
          strokeWidth={1.6}
        />
        <line x1={ML} x2={W - MR} y1={sV.zero} y2={sV.zero} stroke="#999" />
        <text x={4} y={yV + 10} fontSize={9} fill={CERERE}>
          {fmt(vMax)}
        </text>
        {zone.map((z) => (
          <g key={`z${z.nr}`}>
            <rect
              x={x(z.s1_m)}
              y={sV.zero + 2}
              width={Math.max(x(z.s2_m) - x(z.s1_m), 1)}
              height={12}
              fill={z.tip === "reazem" ? ETRIER : CAPACITATE}
              opacity={z.tip === "reazem" ? 0.3 : 0.15}
              stroke={ETRIER}
              strokeWidth={0.6}
            />
            {x(z.s2_m) - x(z.s1_m) > 48 && (
              <text
                x={(x(z.s1_m) + x(z.s2_m)) / 2}
                y={sV.zero + 11}
                fontSize={8.5}
                textAnchor="middle"
                fill={ETRIER}
              >
                Ø{z.phi_mm}/{z.pas_mm}
              </text>
            )}
          </g>
        ))}

        {/* cota de lungime */}
        <text x={ML} y={H - 6} fontSize={9} fill="var(--mut)">
          x = 0
        </text>
        <text x={W - MR} y={H - 6} fontSize={9} textAnchor="end" fill="var(--mut)">
          {mFmt(L)} m
        </text>
        <text
          x={(ML + W - MR) / 2}
          y={H - 6}
          fontSize={9}
          textAnchor="middle"
          fill="var(--mut)"
        >
          {ax.nr_statii ?? st.length} stații de calcul pe ax
          {ax.statii && ax.nr_statii && ax.nr_statii > ax.statii.length
            ? ` (${ax.statii.length} desenate)`
            : ""}
        </text>
      </svg>
    </div>
  );
}
